import { useState, useEffect } from "react";
import { useUser } from "../context/UserContext";
import { clientsEndpoint } from "../constants";

const RecruiterClientDashboard = () => {
  const { user } = useUser();
  const [feedbackPeriod, setFeedbackPeriod] = useState("12months");
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [showFilterModal, setShowFilterModal] = useState(false);

  const [filters, setFilters] = useState({
    timeRange: "All time",
    locations: ["US", "AU"],
    status: [],
  });

  const [availableFilters] = useState({
    timeRanges: ["All time", "Last 7 days", "Last 30 days", "Last 3 months", "Last year"],
    locations: ["US", "AU", "UK", "CA", "DE", "FR", "JP", "IN"],
    statuses: ["Completed", "Pending", "In Progress"],
  });

  const [dashboardData, setDashboardData] = useState({
    metrics: {
      feePerClient: { value: "40%", change: 40, trend: "up" },
      expectedDealValue: { value: "1,210", change: 10, trend: "down" },
      placements: { value: "124", change: 20, trend: "up" },
      avgTimeToFill: { value: "12 Days", change: 20, trend: "up" },
    },
    feedbackChartData: [],
    clientContribution: {
      total: 316,
      clientA: 180,
      client: 136,
    },
    placements: [
      { id: 1, role: "Morph Mind", avatar: null, candidates: 215, feePercent: 40, value: "$240,000", date: "May 22, 2025" },
      { id: 2, role: "Morph Mind", avatar: null, candidates: 215, feePercent: 40, value: "$240,000", date: "May 22, 2025" },
      { id: 3, role: "Morph Mind", avatar: null, candidates: 215, feePercent: 40, value: "$240,000", date: "May 22, 2025" },
      { id: 4, role: "Morph Mind", avatar: null, candidates: 215, feePercent: 40, value: "$240,000", date: "May 22, 2025" },
      { id: 5, role: "Morph Mind", avatar: null, candidates: 215, feePercent: 40, value: "$240,000", date: "May 22, 2025" },
      { id: 6, role: "Morph Mind", avatar: null, candidates: 215, feePercent: 40, value: "$240,000", date: "May 22, 2025" },
      { id: 7, role: "Morph Mind", avatar: null, candidates: 215, feePercent: 40, value: "$240,000", date: "May 22, 2025" },
      { id: 8, role: "Morph Mind", avatar: null, candidates: 215, feePercent: 40, value: "$240,000", date: "May 22, 2025" },
    ],
    totalPlacements: 100,
  });

  const fetchMetrics = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${clientsEndpoint}recruiter-dashboard/metrics/`, {
        headers: {
          Authorization: `Token ${token}`,
          "Content-Type": "application/json",
        },
      });
      if (response.ok) {
        const data = await response.json();
        setDashboardData((prev) => ({ ...prev, metrics: data }));
      }
    } catch (error) {
      console.error("Error fetching metrics:", error);
    }
  };

  const fetchFeedbackChart = async (period) => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${clientsEndpoint}recruiter-dashboard/feedback-chart/?period=${period}`, {
        headers: {
          Authorization: `Token ${token}`,
          "Content-Type": "application/json",
        },
      });
      if (response.ok) {
        const data = await response.json();
        setDashboardData((prev) => ({ ...prev, feedbackChartData: data }));
      }
    } catch (error) {
      console.error("Error fetching feedback chart:", error);
    }
  };

  const fetchClientContribution = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${clientsEndpoint}recruiter-dashboard/client-contribution/`, {
        headers: {
          Authorization: `Token ${token}`,
          "Content-Type": "application/json",
        },
      });
      if (response.ok) {
        const data = await response.json();
        setDashboardData((prev) => ({ ...prev, clientContribution: data }));
      }
    } catch (error) {
      console.error("Error fetching client contribution:", error);
    }
  };

  const fetchPlacements = async (page, search, filterParams) => {
    try {
      const token = localStorage.getItem("token");
      const params = new URLSearchParams({
        page: page.toString(),
        search: search,
        time_range: filterParams.timeRange,
        locations: filterParams.locations.join(","),
        status: filterParams.status.join(","),
      });
      const response = await fetch(`${clientsEndpoint}recruiter-dashboard/placements/?${params}`, {
        headers: {
          Authorization: `Token ${token}`,
          "Content-Type": "application/json",
        },
      });
      if (response.ok) {
        const data = await response.json();
        setDashboardData((prev) => ({
          ...prev,
          placements: data.results || data,
          totalPlacements: data.count || data.length,
        }));
      }
    } catch (error) {
      console.error("Error fetching placements:", error);
    }
  };

  useEffect(() => {
    const loadDashboard = async () => {
      setLoading(true);
      await Promise.all([
        fetchMetrics(),
        fetchFeedbackChart(feedbackPeriod),
        fetchClientContribution(),
        fetchPlacements(currentPage, searchQuery, filters),
      ]);
      setLoading(false);
    };
    loadDashboard();
  }, []);

  useEffect(() => {
    fetchFeedbackChart(feedbackPeriod);
  }, [feedbackPeriod]);

  useEffect(() => {
    fetchPlacements(currentPage, searchQuery, filters);
  }, [currentPage, searchQuery, filters]);

  const getActiveFilters = () => {
    const active = [];
    if (filters.timeRange && filters.timeRange !== "All time") {
      active.push({ key: "timeRange", label: filters.timeRange });
    } else if (filters.timeRange === "All time") {
      active.push({ key: "timeRange", label: "All time" });
    }
    if (filters.locations.length > 0) {
      const label = filters.locations.length > 2
        ? `${filters.locations.slice(0, 2).join(", ")}, +${filters.locations.length - 2}`
        : filters.locations.join(", ");
      active.push({ key: "locations", label });
    }
    filters.status.forEach((s) => {
      active.push({ key: `status_${s}`, label: s, type: "status", value: s });
    });
    return active;
  };

  const removeFilter = (filterItem) => {
    if (filterItem.key === "timeRange") {
      setFilters((prev) => ({ ...prev, timeRange: "" }));
    } else if (filterItem.key === "locations") {
      setFilters((prev) => ({ ...prev, locations: [] }));
    } else if (filterItem.type === "status") {
      setFilters((prev) => ({
        ...prev,
        status: prev.status.filter((s) => s !== filterItem.value),
      }));
    }
  };

  const toggleLocation = (loc) => {
    setFilters((prev) => ({
      ...prev,
      locations: prev.locations.includes(loc)
        ? prev.locations.filter((l) => l !== loc)
        : [...prev.locations, loc],
    }));
  };

  const toggleStatus = (status) => {
    setFilters((prev) => ({
      ...prev,
      status: prev.status.includes(status)
        ? prev.status.filter((s) => s !== status)
        : [...prev.status, status],
    }));
  };

  const totalPages = Math.ceil(dashboardData.totalPlacements / 10);

  const renderMiniChart = (trend) => {
    const path = trend === "down"
      ? "M0 30L20 25L40 35L60 30L80 40L100 35"
      : "M0 45L20 35L40 40L60 30L80 35L100 25";
    return (
      <svg className="w-full h-full" viewBox="0 0 100 60" fill="none">
        <path
          d={path}
          stroke={trend === "down" ? "#ef4444" : "#19bfb7"}
          strokeWidth="1.5"
          fill="none"
        />
      </svg>
    );
  };

  const renderFeedbackChart = () => {
    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    const dataPoints = dashboardData.feedbackChartData.length > 0
      ? dashboardData.feedbackChartData
      : [18, 22, 20, 28, 25, 32, 30, 38, 35, 42, 40, 50];

    return (
      <div className="relative h-56">
        <div className="absolute left-0 top-0 bottom-8 flex flex-col justify-between text-xs text-gray-400 pr-2">
          <span>60</span>
          <span>50</span>
          <span>40</span>
          <span>30</span>
          <span>20</span>
          <span>10</span>
          <span>0</span>
        </div>
        <div className="ml-8 h-full flex flex-col">
          <div className="flex-1 relative">
            <svg className="w-full h-full" viewBox="0 0 440 180" preserveAspectRatio="none">
              <defs>
                <linearGradient id="lineGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor="#19bfb7" />
                  <stop offset="100%" stopColor="#19bfb7" />
                </linearGradient>
              </defs>
              {[0, 30, 60, 90, 120, 150, 180].map((y, i) => (
                <line key={i} x1="0" y1={y} x2="440" y2={y} stroke="#f3f4f6" strokeWidth="1" />
              ))}
              <polyline
                fill="none"
                stroke="url(#lineGradient)"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                points={dataPoints.map((val, i) => `${i * 40},${180 - val * 3}`).join(" ")}
              />
              {dataPoints.map((val, i) => (
                <circle
                  key={i}
                  cx={i * 40}
                  cy={180 - val * 3}
                  r="4"
                  fill="#19bfb7"
                  stroke="white"
                  strokeWidth="2"
                />
              ))}
            </svg>
          </div>
          <div className="flex justify-between text-xs text-gray-400 mt-2 px-0">
            {months.map((m) => (
              <span key={m}>{m}</span>
            ))}
          </div>
          <div className="text-center mt-1">
            <span className="text-xs text-gray-500">Month</span>
          </div>
        </div>
        <div className="absolute left-0 top-1/2 -translate-y-1/2 -rotate-90 origin-left text-xs text-gray-500 whitespace-nowrap" style={{ marginLeft: '-30px' }}>
          Feedbacks
        </div>
      </div>
    );
  };

  const renderDonutChart = () => {
    const { total, clientA, client } = dashboardData.clientContribution;
    const clientAPercent = (clientA / total) * 100;
    const clientPercent = (client / total) * 100;

    const radius = 70;
    const circumference = 2 * Math.PI * radius;
    const clientADash = (clientAPercent / 100) * circumference;
    const clientDash = (clientPercent / 100) * circumference;

    return (
      <div className="flex items-center justify-center gap-8">
        <div className="relative w-48 h-48">
          <svg className="w-full h-full transform -rotate-90" viewBox="0 0 200 200">
            <circle cx="100" cy="100" r={radius} fill="none" stroke="#e5e7eb" strokeWidth="20" />
            <circle
              cx="100"
              cy="100"
              r={radius}
              fill="none"
              stroke="#19bfb7"
              strokeWidth="20"
              strokeDasharray={`${clientADash} ${circumference}`}
              strokeLinecap="round"
            />
            <circle
              cx="100"
              cy="100"
              r={radius}
              fill="none"
              stroke="#d1d5db"
              strokeWidth="20"
              strokeDasharray={`${clientDash} ${circumference}`}
              strokeDashoffset={-clientADash}
              strokeLinecap="round"
            />
          </svg>
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="text-3xl font-semibold text-gray-900">{total}</span>
          </div>
        </div>
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-[#19bfb7]"></div>
            <span className="text-sm text-gray-600">Client A</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-gray-300"></div>
            <span className="text-sm text-gray-600">Client</span>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-[#19bfb7]"></div>
          <p className="mt-4 text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">
            Welcome back, {user ? user.first_name || user.username : 'Guest'}
          </h1>
          <p className="text-sm text-gray-500">
            Here is a complete AI overview of your hiring.
          </p>
        </div>
        <button className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
          </svg>
          Import
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-lg border border-gray-200 p-5 shadow-lg">
          <p className="text-sm text-gray-500 mb-2">Fee % per client</p>
          <p className="text-2xl font-semibold text-gray-900 mb-1">{dashboardData.metrics.feePerClient.value}</p>
          <p className={`text-xs mb-4 ${dashboardData.metrics.feePerClient.change >= 0 ? "text-green-600" : "text-red-600"}`}>
            {dashboardData.metrics.feePerClient.change >= 0 ? "↑" : "↓"} {Math.abs(dashboardData.metrics.feePerClient.change)}% vs last month
          </p>
          <div className="h-16">
            {renderMiniChart(dashboardData.metrics.feePerClient.trend)}
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-5 shadow-lg">
          <p className="text-sm text-gray-500 mb-2">Expected Deal Value</p>
          <p className="text-2xl font-semibold text-gray-900 mb-1">{dashboardData.metrics.expectedDealValue.value}</p>
          <p className={`text-xs mb-4 ${dashboardData.metrics.expectedDealValue.change >= 0 ? "text-green-600" : "text-red-600"}`}>
            {dashboardData.metrics.expectedDealValue.change >= 0 ? "↑" : "↓"} {Math.abs(dashboardData.metrics.expectedDealValue.change)}% vs last month
          </p>
          <div className="h-16">
            {renderMiniChart(dashboardData.metrics.expectedDealValue.trend)}
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-5 shadow-lg">
          <p className="text-sm text-gray-500 mb-2">Placements</p>
          <p className="text-2xl font-semibold text-gray-900 mb-1">{dashboardData.metrics.placements.value}</p>
          <p className={`text-xs mb-4 ${dashboardData.metrics.placements.change >= 0 ? "text-green-600" : "text-red-600"}`}>
            {dashboardData.metrics.placements.change >= 0 ? "↑" : "↓"} {Math.abs(dashboardData.metrics.placements.change)}% vs last month
          </p>
          <div className="h-16">
            {renderMiniChart(dashboardData.metrics.placements.trend)}
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-5 shadow-lg">
          <p className="text-sm text-gray-500 mb-2">Avg Time-to-Fill by client</p>
          <p className="text-2xl font-semibold text-gray-900 mb-1">{dashboardData.metrics.avgTimeToFill.value}</p>
          <p className={`text-xs mb-4 ${dashboardData.metrics.avgTimeToFill.change >= 0 ? "text-green-600" : "text-red-600"}`}>
            {dashboardData.metrics.avgTimeToFill.change >= 0 ? "↑" : "↓"} {Math.abs(dashboardData.metrics.avgTimeToFill.change)}% vs last month
          </p>
          <div className="h-16">
            {renderMiniChart(dashboardData.metrics.avgTimeToFill.trend)}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <h3 className="text-sm font-semibold text-gray-900 mb-4">Feedback speed</h3>
          <div className="flex gap-1 bg-gray-100 rounded-lg p-1 w-fit mb-4">
            {["12months", "30days", "7days", "24hours"].map((period) => (
              <button
                key={period}
                onClick={() => setFeedbackPeriod(period)}
                className={`px-3 py-1.5 text-xs rounded-md transition-colors ${
                  feedbackPeriod === period ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-700"
                }`}
              >
                {period === "12months" ? "12 months" : period === "30days" ? "30 days" : period === "7days" ? "7 days" : "24 hours"}
              </button>
            ))}
          </div>
          {renderFeedbackChart()}
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <h3 className="text-sm font-semibold text-gray-900 mb-4">Client contribution</h3>
          <div className="h-56 flex items-center justify-center">
            {renderDonutChart()}
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200">
        <div className="p-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Placements</h3>
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex flex-wrap items-center gap-2">
              {getActiveFilters().map((filterItem, idx) => (
                <span key={idx} className="flex items-center gap-1 px-3 py-1.5 bg-gray-100 text-gray-700 text-sm rounded-lg">
                  {filterItem.label}
                  <button onClick={() => removeFilter(filterItem)} className="text-gray-400 hover:text-gray-600">
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </span>
              ))}
              <div className="relative">
                <button
                  onClick={() => setShowFilterModal(!showFilterModal)}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                  </svg>
                  More filters
                </button>
                {showFilterModal && (
                  <div className="absolute top-full left-0 mt-2 w-72 bg-white border border-gray-200 rounded-lg shadow-lg z-50 p-4">
                    <div className="mb-4">
                      <p className="text-xs font-medium text-gray-500 mb-2">Time Range</p>
                      <div className="flex flex-wrap gap-2">
                        {availableFilters.timeRanges.map((range) => (
                          <button
                            key={range}
                            onClick={() => setFilters((prev) => ({ ...prev, timeRange: range }))}
                            className={`px-2 py-1 text-xs rounded-md ${
                              filters.timeRange === range ? "bg-[#19bfb7] text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                            }`}
                          >
                            {range}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div className="mb-4">
                      <p className="text-xs font-medium text-gray-500 mb-2">Locations</p>
                      <div className="flex flex-wrap gap-2">
                        {availableFilters.locations.map((loc) => (
                          <button
                            key={loc}
                            onClick={() => toggleLocation(loc)}
                            className={`px-2 py-1 text-xs rounded-md ${
                              filters.locations.includes(loc) ? "bg-[#19bfb7] text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                            }`}
                          >
                            {loc}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div className="mb-4">
                      <p className="text-xs font-medium text-gray-500 mb-2">Status</p>
                      <div className="flex flex-wrap gap-2">
                        {availableFilters.statuses.map((status) => (
                          <button
                            key={status}
                            onClick={() => toggleStatus(status)}
                            className={`px-2 py-1 text-xs rounded-md ${
                              filters.status.includes(status) ? "bg-[#19bfb7] text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                            }`}
                          >
                            {status}
                          </button>
                        ))}
                      </div>
                    </div>
                    <button
                      onClick={() => setShowFilterModal(false)}
                      className="w-full py-2 text-sm text-white bg-[#19bfb7] rounded-lg hover:bg-[#18ada6]"
                    >
                      Apply Filters
                    </button>
                  </div>
                )}
              </div>
            </div>
            <div className="relative">
              <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                type="text"
                placeholder="Search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 pr-4 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#19bfb7] focus:border-transparent w-full md:w-64"
              />
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="w-12 px-4 py-3">
                  <input type="checkbox" className="rounded border-gray-300" />
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Role</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Candidates</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Fee %</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Value</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Date</th>
              </tr>
            </thead>
            <tbody>
              {dashboardData.placements.map((placement) => (
                <tr key={placement.id} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <input type="checkbox" className="rounded border-gray-300" />
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-orange-500 flex items-center justify-center">
                        <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <span className="text-sm font-medium text-gray-900">{placement.role}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">{placement.candidates}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{placement.feePercent}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{placement.value}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{placement.date}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-between p-4 border-t border-gray-200">
          <button
            onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
            disabled={currentPage === 1}
            className="flex items-center gap-1 px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded-lg disabled:opacity-50"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Previous
          </button>
          <div className="flex items-center gap-1">
            {[1, 2, 3].map((page) => (
              <button
                key={page}
                onClick={() => setCurrentPage(page)}
                className={`w-8 h-8 text-sm rounded-lg ${
                  currentPage === page ? "bg-gray-100 text-gray-900 font-medium" : "text-gray-600 hover:bg-gray-50"
                }`}
              >
                {page}
              </button>
            ))}
            {totalPages > 6 && <span className="px-2 text-gray-400">...</span>}
            {totalPages > 3 && [8, 9, 10].filter(p => p <= totalPages).map((page) => (
              <button
                key={page}
                onClick={() => setCurrentPage(page)}
                className={`w-8 h-8 text-sm rounded-lg ${
                  currentPage === page ? "bg-gray-100 text-gray-900 font-medium" : "text-gray-600 hover:bg-gray-50"
                }`}
              >
                {page}
              </button>
            ))}
          </div>
          <button
            onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
            disabled={currentPage === totalPages}
            className="flex items-center gap-1 px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded-lg disabled:opacity-50"
          >
            Next
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
};

export default RecruiterClientDashboard;